// src/components/CatylexSearch/CatylexContractList.tsx

import * as React from "react";
import { useCallback } from "react";
import { CatylexContract } from "../../interfaces/catylexSearchTypes";
import { ExternalLinkIcon } from "./CatylexIcons";
import CatylexDateBadge from "./CatylexDateBadge";
import { roleLabel } from "./catylexFormatters";
import {
  emptyStateStyle,
  containerStyle,
  cardStyle,
  headerRowStyle,
  titleColStyle,
  titleRowStyle,
  titleStyle,
  fileNameStyle,
  getStatusStyle,
  reportLinkStyle,
  fieldGridStyle,
  fieldLabelStyle,
  fieldValueStyle,
  summaryStyle,
} from "./catylexCardStyles";

interface CatylexContractListProps {
  contracts: CatylexContract[];
  total: number;
}

interface FieldRow {
  key: string;
  label: string;
  value: string;
}

/** Build the label/value rows (parties + concepts) shown beneath the title. */
const buildFieldRows = (contract: CatylexContract): FieldRow[] => {
  const rows: FieldRow[] = [];
  (contract.parties || []).forEach((party, idx) => {
    if (!party?.name) return;
    rows.push({ key: `party-${idx}`, label: roleLabel(party.role), value: party.name });
  });
  (contract.metadata || []).forEach((concept, idx) => {
    if (!concept?.values?.length) return;
    rows.push({
      key: `concept-${idx}`,
      label: concept.concept_name,
      value: concept.values.join(", "),
    });
  });
  return rows;
};

const CatylexContractList: React.FC<CatylexContractListProps> = ({ contracts, total }) => {
  const handleOpenReport = useCallback((url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (/^https?:\/\//i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  if (!contracts || contracts.length === 0) {
    return <div style={emptyStateStyle}>No contracts found matching your search</div>;
  }

  return (
    <div style={containerStyle}>
      {contracts.map((contract) => {
        const displayTitle =
          contract.display_title && contract.display_title.trim()
            ? contract.display_title
            : contract.file_name || contract.title;
        const fileName = contract.file_name || contract.title;
        const showFileName = !!fileName && fileName !== displayTitle;
        const fieldRows = buildFieldRows(contract);

        return (
          <div key={contract.id} style={cardStyle}>
            <div style={headerRowStyle}>
              <CatylexDateBadge isoDate={contract.agreement_date} />
              <div style={titleColStyle}>
                <div style={titleRowStyle}>
                  <p style={titleStyle} title={displayTitle}>
                    {displayTitle}
                  </p>
                  {contract.all_terms_report_url && (
                    <button
                      style={reportLinkStyle}
                      onClick={(e) => handleOpenReport(contract.all_terms_report_url!, e)}
                      aria-label={`Open terms report for ${displayTitle}`}
                      title="View terms report in a new window"
                    >
                      <ExternalLinkIcon />
                    </button>
                  )}
                  <span style={getStatusStyle(contract.status)}>{contract.status}</span>
                </div>
                {showFileName && (
                  <p style={fileNameStyle} title={fileName}>
                    {fileName}
                  </p>
                )}
              </div>
            </div>

            {fieldRows.length > 0 && (
              <div style={fieldGridStyle}>
                {fieldRows.map((row) => (
                  <React.Fragment key={row.key}>
                    <span style={fieldLabelStyle}>{row.label}</span>
                    <span style={fieldValueStyle} title={row.value}>
                      {row.value}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {total > contracts.length && (
        <p style={summaryStyle}>
          Showing {contracts.length} of {total} contracts
        </p>
      )}
    </div>
  );
};

export default CatylexContractList;
